/**
 * ONE passkey tap for a locked browser (docs/e2e.md "Unlock via a machine").
 *
 * The same WebAuthn ceremony unlocks the app (app lock / idle lock) AND the encrypted content:
 *
 * 1. Before the tap the browser prepares its unlock request (local only: device key, ECDH content
 *    key, the directory head it verified, a fresh nonce, ≤ 5 min expiry). The ceremony's challenge
 *    IS the request digest, so it is fresh and unpredictable for the app lock, and binds the
 *    request for the Mac. The ceremony also asks for the passkey's PRF secret.
 * 2. The app lock verifies the assertion against this browser's pinned passkeys (as before).
 * 3. Content: with a PRF result the passkey's escrow opens locally. Otherwise (or when that
 *    passkey has no escrow) the SAME assertion goes to the account's key-holding daemon ("Unlock
 *    via Areks-Macbook-Pro.local"), which verifies it and wraps the keys to this browser. No second
 *    button, no second tap. The browser then self-joins, so later rotations need nothing.
 *
 * A returning browser's sign-in carries the same request inside its login batch
 * (faceid-signin.ts), so signing in is the one tap too.
 */
import {
  type ApprovalBatchItem,
  createUnlockRequestStatement,
  isSessionEndorsement,
  passkeyPrfSaltV2,
  randomBytes,
  type SignedUnlockRequest,
  signUnlockRequest,
  type TrustStore,
  type UnlockGrant,
  type UnlockRefusal,
  type UnlockRequestStatement,
  unlockRequestChallenge,
  unlockRequestDigest,
  type WebAuthnAssertion,
} from '@viko/crypto'
import { type Api, ApiError } from './api.js'
import { keysApi } from './content-keys-api.js'
import { probeContent } from './content-lock.js'
import type { BrowserContentSession } from './content-session.js'
import { loadContentTrust, pinFromVerifiedAssertion } from './content-trust.js'
import { loadDevice, type StoredDevice } from './device-key.js'
import { unlockWithPrf } from './faceid-signin.js'
import type { PasskeyProvider } from './passkeys.js'

export { rememberAccount, rememberedAccount } from './last-account.js'

export type MachineFailureCode =
  | 'no-machine'
  | 'expired'
  | 'recovery-hold'
  | 'refused'
  | 'not-trusted'
  | 'passkey-unverified'
  | 'rate-limited'
  | 'failed'
export interface MachineFailure {
  code: MachineFailureCode
  machineName?: string
  reason?: UnlockRefusal
  /** The precise cause (shown as "Details" so a failure is diagnosable, never a generic dead end). */
  detail?: string
}
export type ContentUnlockResult =
  | { status: 'unlocked'; via: 'prf' | 'machine' | 'already'; machineName?: string; prf: boolean }
  | {
      status: 'locked'
      fallback?: 'no-prf' | 'no-escrow' | 'still-locked'
      failure?: MachineFailure
      prf: boolean
    }
export type MachineResult =
  | { status: 'unlocked'; via: 'machine'; machineName: string }
  | { status: 'locked'; failure: MachineFailure }
export type UnlockPhase = { step: 'passkey' } | { step: 'machine'; machineName: string }

export interface CeremonyDeps {
  api: Api
  passkeys: Pick<PasskeyProvider, 'assertWithPrf' | 'assert'>
  onPhase?: (p: UnlockPhase) => void
  origin?: string
  pollMs?: number
  sleep?: (ms: number) => Promise<void>
  now?: () => number
}

// ---- prepare (no ceremony, no network) ---------------------------------------------------------

export interface PreparedUnlock {
  accountId: string
  statement: UnlockRequestStatement
  device: StoredDevice
  challenge: Uint8Array
  digest: string
}

/** Why this browser could not build an unlock request (surfaced as the failure detail). */
export class UnlockPrepError extends Error {}

/**
 * The request for this browser; throws UnlockPrepError when the machine path can't apply (no
 * device key, no trusted — non-session — endorsement). Local passkey pins are NOT needed to build
 * it: a browser without them bootstraps them from the ceremony's own assertion (unlockCeremony).
 */
export async function prepareUnlock(
  session: Pick<BrowserContentSession, 'vault'>,
  accountId: string,
  /** Sign-in: the device's endorsement is in the same ceremony's batch, not stored yet. */
  opts: { signIn?: boolean } = {},
): Promise<PreparedUnlock | undefined> {
  const device = await loadDevice()
  if (!device) throw new UnlockPrepError('no-device-key')
  if (!opts.signIn && !device.endorsement) throw new UnlockPrepError('no-endorsement')
  if (!opts.signIn && isSessionEndorsement(device.endorsement!.statement))
    throw new UnlockPrepError('session-endorsement')
  const identity = await session.vault.ensureIdentity(accountId)
  const head = (await session.vault.state(accountId))?.checkpoint
  const statement = await createUnlockRequestStatement({
    accountId,
    deviceKeyId: device.deviceKeyId,
    agreementKey: identity.publicJwk,
    directory: head
      ? { revision: head.revision, digest: head.digest, epoch: head.statement.epoch }
      : { revision: 0, digest: null, epoch: 0 },
  })
  return {
    accountId,
    statement,
    device,
    challenge: await unlockRequestChallenge(statement),
    digest: await unlockRequestDigest(statement),
  }
}

// ---- the one ceremony --------------------------------------------------------------------------

/**
 * Run the single passkey ceremony. Resolves once the passkey verified (the app is unlocked);
 * `content` settles the content unlock (PRF locally, else via a key-holding machine).
 *
 * A browser with no local passkey pins (signed in with a passkey created elsewhere, e.g. a
 * 1Password passkey without PRF) bootstraps them from this same assertion: the hub names the
 * credential's public key, and it is pinned only if it verifies this fresh assertion locally
 * (content-trust.ts pinFromVerifiedAssertion). Still one tap.
 */
export async function unlockCeremony(
  session: BrowserContentSession,
  deps: CeremonyDeps,
): Promise<{ content: Promise<ContentUnlockResult> }> {
  const accountId = await session.accountId()
  let trust = await loadContentTrust(accountId).catch(() => undefined)
  let prepError: string | undefined
  const prepared = await prepareUnlock(session, accountId).catch((e: unknown) => {
    prepError = e instanceof Error ? e.message : String(e)
    return undefined
  })
  const challenge = prepared?.challenge ?? randomBytes(32)
  // No pins yet: ask for the passkey that endorsed this browser (its endorsement must verify
  // against the pin we are about to make, or the Mac path can't install the keys).
  const live = trust
    ? trust.credentials().map((c) => c.credentialId)
    : prepared?.device.endorsement
      ? [prepared.device.endorsement.assertion.credentialId]
      : []
  deps.onPhase?.({ step: 'passkey' })
  const prfSalt = await passkeyPrfSaltV2()
  const { assertion, prf } = deps.passkeys.assertWithPrf
    ? await deps.passkeys.assertWithPrf(challenge, {
        ...(trust ? { rpId: trust.rp.rpId } : {}),
        ...(live.length ? { allowCredentials: live } : {}),
        prfSalt,
      })
    : { assertion: await deps.passkeys.assert(challenge, trust ? { rpId: trust.rp.rpId } : {}) }
  // The app lock's check, unchanged: a UV assertion by a passkey this browser pinned.
  if (trust && !(await trust.verifyPasskeyApproval(assertion, challenge)).ok) {
    prf?.fill(0)
    throw new Error('Passkey not recognized on this device')
  }
  let bootFailure: MachineFailure | undefined
  if (!trust && prepared) {
    const boot = await bootstrapPins(deps, accountId, prepared, assertion, challenge)
    if (boot.ok) trust = boot.trust
    else bootFailure = boot.failure
  }
  return { content: finish(session, deps, prepared, assertion, prf, prepError, bootFailure) }
}

/** Pin the ceremony's passkey from its own locally verified assertion (no second tap). */
async function bootstrapPins(
  deps: CeremonyDeps,
  accountId: string,
  prepared: PreparedUnlock,
  assertion: WebAuthnAssertion,
  challenge: Uint8Array,
): Promise<{ ok: true; trust: TrustStore } | { ok: false; failure: MachineFailure }> {
  const origin = deps.origin ?? globalThis.location?.origin ?? ''
  const boot = await pinFromVerifiedAssertion({
    accountId,
    assertion,
    challenge,
    origin,
    lookup: (id) => keysApi(deps.api).unlockCredential(id),
  }).catch((e: unknown) => ({
    ok: false as const,
    reason: 'credential-unavailable' as const,
    detail: e instanceof Error ? e.message : String(e),
  }))
  if (!boot.ok) {
    const failure: MachineFailure =
      boot.reason === 'recovery-hold'
        ? { code: 'recovery-hold', detail: 'recovery-hold' }
        : {
            code: 'passkey-unverified',
            detail: boot.detail ? `${boot.reason}: ${boot.detail}` : boot.reason,
          }
    return { ok: false, failure }
  }
  // The Mac path installs keys only for a browser whose endorsement verifies against its pins.
  const endorsement = prepared.device.endorsement
  if (!endorsement || !(await boot.trust.addEndorsement(endorsement)).ok)
    return {
      ok: false,
      failure: { code: 'passkey-unverified', detail: 'endorsed-by-another-passkey' },
    }
  return { ok: true, trust: boot.trust }
}

async function probe(session: BrowserContentSession) {
  return probeContent(session).catch(() => 'locked' as const)
}

async function finish(
  session: BrowserContentSession,
  deps: CeremonyDeps,
  prepared: PreparedUnlock | undefined,
  assertion: WebAuthnAssertion,
  prf: Uint8Array | undefined,
  prepError?: string,
  /** Pin bootstrap failed: the machine path can't run (the PRF path still may). */
  bootFailure?: MachineFailure,
): Promise<ContentUnlockResult> {
  const hadPrf = !!prf
  try {
    if (prf) {
      const r = await unlockWithPrf(
        session,
        { credentialId: assertion.credentialId, output: prf },
        deps.origin,
      ).catch(() => 'no-escrow' as const)
      if ((r === 'unlocked' || r === 'already') && (await probe(session)) === 'unlocked')
        return { status: 'unlocked', via: 'prf', prf: true }
    }
  } finally {
    prf?.fill(0)
  }
  if ((await probe(session)) === 'unlocked')
    return { status: 'unlocked', via: 'already', prf: hadPrf }
  const fallback = hadPrf ? ('no-escrow' as const) : ('no-prf' as const)
  if (!prepared)
    return {
      status: 'locked',
      fallback,
      failure: { code: 'not-trusted', ...(prepError ? { detail: prepError } : {}) },
      prf: hadPrf,
    }
  if (bootFailure) return { status: 'locked', fallback, failure: bootFailure, prf: hadPrf }
  const r = await viaMachine(session, deps, prepared, assertion)
  return r.status === 'locked' ? { ...r, fallback, prf: hadPrf } : { ...r, prf: hadPrf }
}

// ---- the machine path --------------------------------------------------------------------------

const RELAY_FAILURE: Record<string, MachineFailureCode> = {
  unlock_no_machine: 'no-machine',
  unlock_recovery_hold: 'recovery-hold',
  unlock_rate_limited: 'rate-limited',
  unlock_expired: 'expired',
  unlock_unknown_device: 'not-trusted',
  unlock_session_device: 'not-trusted',
}

function relayFailure(e: unknown): MachineFailure {
  if (e instanceof ApiError) return { code: RELAY_FAILURE[e.code] ?? 'failed', detail: e.code }
  return { code: 'failed', detail: e instanceof Error ? e.message : String(e) }
}

/**
 * Send the signed request (the ceremony's assertion; `batch` when it came from the sign-in
 * batch), wait for a key-holding machine's answer, install the commitment-checked keys and join.
 */
export async function viaMachine(
  session: BrowserContentSession,
  deps: Omit<CeremonyDeps, 'passkeys'>,
  p: PreparedUnlock,
  assertion: WebAuthnAssertion,
  batch?: ApprovalBatchItem[],
): Promise<MachineResult> {
  const relay = keysApi(deps.api)
  const signed: SignedUnlockRequest = await signUnlockRequest(
    p.statement,
    p.device,
    assertion,
    batch,
  )
  let started: Awaited<ReturnType<typeof relay.unlockRequest>>
  try {
    started = await relay.unlockRequest(signed)
  } catch (e) {
    return { status: 'locked', failure: relayFailure(e) }
  }
  const machineName = started.machines.map((m) => m.name).join(' or ') || 'your Mac'
  deps.onPhase?.({ step: 'machine', machineName })
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const now = deps.now ?? Date.now
  for (;;) {
    await sleep(deps.pollMs ?? 1000)
    let st: Awaited<ReturnType<typeof relay.unlockStatus>>
    try {
      st = await relay.unlockStatus(started.requestId)
    } catch (e) {
      if (e instanceof ApiError && e.status === 0 && now() <= started.expiresAt) continue
      return { status: 'locked', failure: { ...relayFailure(e), machineName } }
    }
    const name = st.machineName ?? machineName
    if (st.status === 'pending') {
      if (now() > started.expiresAt + 5_000)
        return { status: 'locked', failure: { code: 'expired', machineName } }
      continue
    }
    if (st.status === 'granted' && st.grant) {
      try {
        await install(session, st.grant as UnlockGrant, p.digest, st.machineId ?? '')
      } catch (e) {
        return {
          status: 'locked',
          failure: {
            code: 'failed',
            machineName: name,
            detail: e instanceof Error ? e.message : String(e),
          },
        }
      }
      return (await probe(session)) === 'unlocked'
        ? { status: 'unlocked', via: 'machine', machineName: name }
        : { status: 'locked', failure: { code: 'failed', machineName: name } }
    }
    if (st.status === 'refused')
      return {
        status: 'locked',
        failure: {
          code: st.reason === 'recovery-hold' ? 'recovery-hold' : 'refused',
          machineName: name,
          ...(st.reason ? { reason: st.reason } : {}),
        },
      }
    if (st.status === 'expired')
      return { status: 'locked', failure: { code: 'expired', machineName } }
    return { status: 'locked', failure: { code: 'failed', machineName } }
  }
}

async function install(
  session: BrowserContentSession,
  grant: UnlockGrant,
  digest: string,
  machineId: string,
) {
  await session.exclusive(async (ctx) => {
    const { trust, device } = await ctx.local()
    // Catch up first: the daemon may be ahead of this browser's pin (commitments come from here).
    await ctx.refresh(trust, device)
    await ctx.vault.installGranted(ctx.accountId, device.deviceKeyId, grant, digest, machineId)
    // Trusted browsers follow rotations: join now with the ADK proof (no tap).
    await ctx.joinTrusted(trust, device).catch(() => false)
  })
}

/** User-facing text for a machine-path failure (≥ 14px in the panel). */
export function machineFailureText(f: MachineFailure): string {
  const mac = f.machineName ?? 'your Mac'
  switch (f.code) {
    case 'no-machine':
      return f.machineName
        ? `${mac} isn’t reachable right now. Open it (Viko starts automatically), then unlock again.`
        : 'None of your machines that hold your key is reachable right now. Open your Mac (Viko starts automatically), then unlock again.'
    case 'expired':
      return `${mac} didn’t answer within 5 minutes. Check that Viko is running on it, then unlock again.`
    case 'recovery-hold':
      return 'This passkey was created during a recovery and is on its 24-hour hold. Approve the recovery from another signed-in device, or wait for the hold to end.'
    case 'rate-limited':
      return 'Too many unlock requests in the last hour. Wait a few minutes, then try again.'
    case 'not-trusted':
      return notTrustedText(mac, f.detail)
    case 'passkey-unverified':
      return passkeyUnverifiedText(f.detail)
    case 'refused':
      return refusalText(mac, f.reason)
    case 'failed':
      // Pins bootstrapped from one passkey can't verify key history approved by another one.
      if (
        f.detail === 'unapproved directory signer' ||
        f.detail === 'directory genesis requires pinned passkey'
      )
        return `${mac} sent your keys, but your key history was approved with another of your passkeys, which this browser can’t verify yet. Sign out, sign in with that passkey (the one you set Viko up with), then unlock again, or use your recovery kit.`
      return `Unlocking via ${mac} didn’t finish. Try again.`
  }
}

/** A browser that can't send an unlock request at all (prepareUnlock): say exactly why. */
function notTrustedText(mac: string, detail: string | undefined): string {
  switch (detail) {
    case 'session-endorsement':
      return `This browser is signed in for this session only, so ${mac} won’t send it your keys. Sign out, then sign in again and choose Trust this device.`
    case 'no-device-key':
    case 'no-endorsement':
      return 'This browser has no trusted device key (its storage may have been cleared). Sign out, then sign in again and choose Trust this device.'
    default:
      return `This browser can’t ask ${mac} for your keys yet. Try again; if it keeps failing, sign out and sign in again.`
  }
}

/** The pin bootstrap refused the passkey (content-trust.ts pinFromVerifiedAssertion). */
function passkeyUnverifiedText(detail: string | undefined): string {
  const reason = detail?.split(':')[0]
  switch (reason) {
    case 'not-on-account':
    case 'wrong-account':
      return 'That passkey isn’t one of this account’s passkeys. Unlock again and pick the passkey you sign in to Viko with.'
    case 'revoked-credential':
      return 'That passkey was revoked. Unlock again with another of your passkeys, or use your recovery kit.'
    case 'credential-unavailable':
      return 'Viko couldn’t look up your passkey (are you offline?). Unlock again in a moment.'
    case 'endorsed-by-another-passkey':
      return 'This browser was trusted with a different passkey. Unlock again with that passkey, or sign out and sign in again with this one.'
    default:
      return 'Your passkey’s answer didn’t verify, so this browser didn’t trust it. Unlock again; if it keeps failing, use your recovery kit.'
  }
}

function refusalText(mac: string, reason: UnlockRefusal | undefined): string {
  switch (reason) {
    case 'no-key':
    case 'stale-key':
      return `${mac} doesn’t hold your newest key yet. Open Viko on a browser that shows your work, or use your recovery kit.`
    case 'revoked-device':
    case 'revoked-credential':
      return `${mac} refused: this browser or passkey was revoked. Sign in again from a trusted device.`
    case 'quarantined':
      return `${mac} refused: this browser’s earlier key join couldn’t be verified. Remove it in You › Security › Devices and sign in again.`
    case 'expired':
    case 'replayed':
      return `${mac} refused an outdated request. Unlock again.`
    default:
      return `${mac} refused the request (${reason ?? 'unknown reason'}).`
  }
}
